/** O fio do BCF ao vivo: o websocket que avisa quando alguma coisa muda.
 *
 *  O servidor (`backend/modules/bcf/ao_vivo.py`) não reenvia dados: diz
 *  que um envio gravou alterações (`mudou`), que alguém pegou num tópico
 *  ou o largou (`bloqueio`), e quem tem o BCF aberto (`presenca`). Os
 *  dados vêm sempre pela API de sincronização. Um aviso perdido não é
 *  trabalho perdido: ao voltar a ligar, puxa-se desde a última versão
 *  vista.
 *
 *  Este ficheiro trata só do fio: abrir, validar o que chega, reconectar
 *  sozinho com espera crescente, e parar quando o servidor recusa por
 *  uma razão que não se resolve a tentar outra vez.
 */

import { lerRecusa } from '@viewer/room/roomClient'
import { vistaRecebida, type VistaRecebida } from '@core/sessoes/acompanhar'

export interface PresenteAoVivo {
  /** Uma entrada por janela. */
  id: number
  utilizador: string
  nome: string
}

export type MensagemDoCanal =
  | { tipo: 'entrou'; id: number; utilizador: string }
  | { tipo: 'presenca'; participantes: PresenteAoVivo[] }
  | { tipo: 'mudou'; seq: number; cliente: string | null }
  | {
      tipo: 'bloqueio'
      topic_guid: string
      /** Sem utilizador, o tópico foi largado. */
      utilizador: string | null
      nome: string | null
      expira: string | null
    }
  /** Só no canal de uma sessão: a vista de outra janela, para a
   *  acompanhar (core/sessoes/acompanhar.ts). */
  | ({ tipo: 'vista' } & VistaRecebida)

const eTexto = (v: unknown): v is string => typeof v === 'string'
const eTextoOuNulo = (v: unknown): v is string | null => v === null || typeof v === 'string'

/** O que chega pelo socket é dado: valida-se antes de lhe tocar. */
export function mensagemDoCanal(v: unknown): MensagemDoCanal | null {
  if (!v || typeof v !== 'object') return null
  const m = v as Record<string, unknown>
  switch (m.tipo) {
    case 'entrou':
      return typeof m.id === 'number' && eTexto(m.utilizador)
        ? { tipo: 'entrou', id: m.id, utilizador: m.utilizador }
        : null
    case 'presenca': {
      if (!Array.isArray(m.participantes)) return null
      const participantes: PresenteAoVivo[] = []
      for (const p of m.participantes as unknown[]) {
        const r = p as Record<string, unknown> | null
        if (r && typeof r.id === 'number' && eTexto(r.utilizador) && eTexto(r.nome)) {
          participantes.push({ id: r.id, utilizador: r.utilizador, nome: r.nome })
        }
      }
      return { tipo: 'presenca', participantes }
    }
    case 'mudou':
      return typeof m.seq === 'number' && eTextoOuNulo(m.cliente ?? null)
        ? { tipo: 'mudou', seq: m.seq, cliente: (m.cliente as string | null) ?? null }
        : null
    case 'bloqueio':
      return eTexto(m.topic_guid)
        && eTextoOuNulo(m.utilizador ?? null)
        && eTextoOuNulo(m.nome ?? null)
        && eTextoOuNulo(m.expira ?? null)
        ? {
            tipo: 'bloqueio',
            topic_guid: m.topic_guid,
            utilizador: (m.utilizador as string | null) ?? null,
            nome: (m.nome as string | null) ?? null,
            expira: (m.expira as string | null) ?? null,
          }
        : null
    case 'vista': {
      const v = vistaRecebida(m)
      return v ? { tipo: 'vista', ...v } : null
    }
    default:
      return null
  }
}

/** O endereço do canal sai do mesmo sítio que o resto da API, com o
 *  esquema trocado para websocket. */
export function urlDoCanal(
  projeto: string,
  base: string = import.meta.env.VITE_API_BASE_URL ?? '',
): string {
  const raiz = base || window.location.origin
  const ws = raiz.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:')
  return `${ws.replace(/\/$/, '')}/api/modules/bcf/sync/ao-vivo?projeto=${encodeURIComponent(projeto)}`
}

/** Fechos que são uma recusa do servidor e não uma falha de rede: sem
 *  sessão (4401), sem acesso ao módulo ou origem recusada (4403), BCF
 *  inválido (4400), regra violada (1008). Tentar outra vez só repetia a
 *  recusa. O 1013 ("tente mais tarde") volta a tentar, com a espera
 *  normal. */
export const CODIGOS_DE_RECUSA: ReadonlySet<number> = new Set([1008, 4400, 4401, 4403])

/** Espera antes de voltar a tentar: 1, 2, 4, 8, 16 e depois 30 s. */
export function esperaMs(tentativa: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.min(tentativa, 5))
}

/** Uma mensagem pequena de vez em quando, para a ligação não ficar
 *  parada atrás de um proxy e para o browser dar por uma ligação morta.
 *  O servidor ignora-a. */
export const PING_MS = 25_000

export type EstadoDoCanal = 'a-ligar' | 'ligado' | 'desligado' | 'recusado'

export interface OpcoesDoCanal {
  projeto: string
  aoReceber: (m: MensagemDoCanal) => void
  aoMudarEstado: (e: EstadoDoCanal, motivo?: string) => void
  /** Trocáveis nos testes. */
  criarSocket?: (url: string) => WebSocket
  relogio?: {
    setTimeout: (f: () => void, ms: number) => unknown
    clearTimeout: (id: unknown) => void
    setInterval: (f: () => void, ms: number) => unknown
    clearInterval: (id: unknown) => void
  }
  base?: string
}

export interface LigacaoDoCanal {
  /** Volta a tentar já (a rede voltou), em vez de esperar o resto da
   *  espera. Sem efeito se estiver ligado ou recusado. */
  tentarJa: () => void
  fechar: () => void
  estado: () => EstadoDoCanal
  /** Manda uma mensagem pelo canal, se estiver ligado. O servidor só a
   *  reenvia no canal de uma sessão (a vista, para acompanhar). */
  enviar: (m: Record<string, unknown>) => boolean
}

const ABERTO = 1

export function ligarAoCanal(opcoes: OpcoesDoCanal): LigacaoDoCanal {
  const criar = opcoes.criarSocket ?? ((url: string) => new WebSocket(url))
  const relogio = opcoes.relogio ?? {
    setTimeout: (f: () => void, ms: number) => window.setTimeout(f, ms),
    clearTimeout: (id: unknown) => window.clearTimeout(id as number),
    setInterval: (f: () => void, ms: number) => window.setInterval(f, ms),
    clearInterval: (id: unknown) => window.clearInterval(id as number),
  }
  let socket: WebSocket | null = null
  let estado: EstadoDoCanal = 'a-ligar'
  let tentativa = 0
  let fechado = false
  let espera: unknown = null
  let ping: unknown = null

  const mudar = (e: EstadoDoCanal, motivo?: string) => {
    estado = e
    opcoes.aoMudarEstado(e, motivo)
  }

  const pararPing = () => {
    if (ping !== null) relogio.clearInterval(ping)
    ping = null
  }

  const agendar = () => {
    if (fechado) return
    mudar('desligado')
    const ms = esperaMs(tentativa)
    tentativa += 1
    espera = relogio.setTimeout(() => {
      espera = null
      abrir()
    }, ms)
  }

  const abrir = () => {
    if (fechado) return
    mudar('a-ligar')
    let s: WebSocket
    try {
      s = criar(urlDoCanal(opcoes.projeto, opcoes.base))
    } catch {
      agendar()
      return
    }
    socket = s
    s.onopen = () => {
      tentativa = 0
      mudar('ligado')
      pararPing()
      ping = relogio.setInterval(() => {
        if (socket && socket.readyState === ABERTO) socket.send('{"tipo":"ping"}')
      }, PING_MS)
    }
    s.onmessage = (ev: MessageEvent) => {
      let lido: unknown
      try {
        lido = JSON.parse(String(ev.data))
      } catch {
        return
      }
      // A recusa dita antes do fecho (backend/core/canais.py): atrás do
      // proxy de produção, o fecho com o código não chegava.
      const recusa = lerRecusa(lido)
      if (recusa) {
        if (CODIGOS_DE_RECUSA.has(recusa.codigo)) {
          fechado = true
          if (socket === s) socket = null
          pararPing()
          mudar('recusado', recusa.motivo || 'O servidor recusou a ligação ao vivo.')
          s.close()
        }
        return
      }
      const m = mensagemDoCanal(lido)
      if (m) opcoes.aoReceber(m)
    }
    s.onclose = (ev?: CloseEvent) => {
      if (socket === s) socket = null
      pararPing()
      if (fechado) return
      if (ev && CODIGOS_DE_RECUSA.has(ev.code)) {
        fechado = true
        mudar('recusado', ev.reason || 'O servidor recusou a ligação ao vivo.')
        return
      }
      agendar()
    }
    s.onerror = () => {
      // O `onclose` vem sempre a seguir; é lá que se reconecta.
    }
  }

  abrir()

  return {
    tentarJa: () => {
      if (fechado || estado !== 'desligado' || espera === null) return
      relogio.clearTimeout(espera)
      espera = null
      tentativa = 0
      abrir()
    },
    fechar: () => {
      fechado = true
      if (espera !== null) relogio.clearTimeout(espera)
      espera = null
      pararPing()
      // Dizer que se sai antes de fechar: atrás do proxy, só o fecho
      // demora a chegar, e os outros continuavam a ver-nos na lista.
      if (socket && socket.readyState === ABERTO) socket.send('{"tipo":"saio"}')
      socket?.close()
      socket = null
      mudar('desligado')
    },
    estado: () => estado,
    enviar: (m) => {
      if (!socket || socket.readyState !== ABERTO) return false
      try {
        socket.send(JSON.stringify(m))
        return true
      } catch {
        return false
      }
    },
  }
}
